import { useEscapeClosableDropdown } from '@/hooks/useEscapeClosableDropdown';
import { useUserStore } from '@/store/use-user-store';
import { useProfileDisplayName } from '@/utils/profile-variables';
import { Icon } from '@iconify/react';
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
  Spinner,
} from '@nextui-org/react';
import { useMediaQuery } from 'usehooks-ts';

interface SoulMapAccountProps {
  /**
   * Owned by the page, which has to leave the chat before the session ends and
   * which outlives this menu (it unmounts once logged out).
   */
  onLogout: () => void;
  isLoggingOut?: boolean;
  disabled?: boolean;
}

/**
 * Account menu for the Soul Map consumer flow, matching the enterprise header:
 * icon, name and chevron, opening to name, email and Log Out. Log Out only ends
 * the Soul Map session; it never signs the user out of Google itself.
 */
export function SoulMapAccount({
  onLogout,
  isLoggingOut = false,
  disabled,
}: SoulMapAccountProps) {
  const user = useUserStore((state) => state.user);
  // Logout clears the query cache after lifting its request guard; a mounted
  // query would refetch then without credentials (401, then a second logout).
  const { fullName, isReady } = useProfileDisplayName({
    enabled: !isLoggingOut,
  });
  const accountDropdown = useEscapeClosableDropdown();
  const isMobile = useMediaQuery('(max-width: 768px)');

  if (!user?.email) return null;

  const displayName =
    (isReady && fullName) ||
    `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() ||
    user.email;

  return (
    <Dropdown {...accountDropdown.dropdownProps}>
      <DropdownTrigger>
        <Button
          {...accountDropdown.triggerProps}
          aria-label={`Account: ${displayName}`}
          className="justify-start text-default-500 data-[hover=true]:text-foreground pr-0 min-w-0 gap-0 sm:gap-2"
          variant="light"
          endContent={
            isLoggingOut ? (
              <Spinner size="sm" />
            ) : (
              <Icon
                className="flex-none text-default-500"
                icon="lsicon:down-outline"
                width={24}
              />
            )
          }
          isDisabled={disabled || isLoggingOut}
        >
          <div className="flex items-center gap-2">
            <Icon
              className="flex-none text-default-500"
              icon="mdi-light:account"
              width={24}
            />
            {!isMobile && (
              <p className="max-w-[180px] truncate text-small font-medium text-foreground text-start">
                {displayName}
              </p>
            )}
          </div>
        </Button>
      </DropdownTrigger>
      <DropdownMenu aria-label="Account actions">
        <DropdownSection showDivider aria-label="Account">
          <DropdownItem key="account-info" className="py-2" isReadOnly>
            <div className="flex flex-col items-start">
              <span className="text-small font-medium truncate max-w-[220px]">
                {displayName}
              </span>
              <span className="text-xs text-default-500 truncate max-w-[220px]">
                {user.email}
              </span>
            </div>
          </DropdownItem>
        </DropdownSection>
        <DropdownItem
          key="logout"
          className="text-danger"
          onPress={onLogout}
          startContent={<Icon icon="ic:baseline-logout" width={20} />}
        >
          Log Out
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
}
